import { CSSProperties, useId } from "react";

import { Scene, Cursor, Tile } from "./common";
import styles from "./locator.module.scss";

export type LocatorTarget =
  | "grid"
  | "tabs"
  | "search"
  | "filters"
  | "insights"
  | "lpTab"
  | "granularity"
  | "status"
  | "modelJobs"
  | "cart"
  | "snapshots"
  | "share"
  | "export"
  | "more";

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Geometry of the page wireframe (viewBox 240 x 200).
const HEADER_ICONS: Record<
  "cart" | "snapshots" | "share" | "export" | "more",
  number
> = { cart: 150, snapshots: 166, share: 182, export: 198, more: 214 };

const TARGETS: Record<LocatorTarget, Box> = {
  grid: { x: 14, y: 74, w: 136, h: 96 },
  tabs: { x: 14, y: 30, w: 56, h: 10 },
  search: { x: 14, y: 48, w: 110, h: 18 },
  filters: { x: 130, y: 48, w: 50, h: 18 },
  insights: { x: 160, y: 74, w: 66, h: 96 },
  lpTab: { x: 194, y: 78, w: 28, h: 12 },
  granularity: { x: 150, y: 177, w: 76, h: 12 },
  status: { x: 78, y: 17, w: 28, h: 12 },
  modelJobs: { x: 112, y: 17, w: 32, h: 12 },
  cart: { x: HEADER_ICONS.cart, y: 17, w: 12, h: 12 },
  snapshots: { x: HEADER_ICONS.snapshots, y: 17, w: 12, h: 12 },
  share: { x: HEADER_ICONS.share, y: 17, w: 12, h: 12 },
  export: { x: HEADER_ICONS.export, y: 17, w: 12, h: 12 },
  more: { x: HEADER_ICONS.more, y: 17, w: 12, h: 12 },
};

interface LocatorSceneProps {
  target: LocatorTarget;
  label: string;
}

// A wireframe of the dataset page. The cursor travels to the target control and highlights it.
export const LocatorScene = ({ target, label }: LocatorSceneProps) => {
  const maskId = useId();
  const box = TARGETS[target];
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  const pad = 3;
  const calloutBelow = box.y < 60;
  const calloutWidth = label.length * 4.6 + 16;
  const calloutX = Math.min(
    Math.max(cx - calloutWidth / 2, 6),
    240 - calloutWidth - 6,
  );
  const calloutY = calloutBelow ? box.y + box.h + 10 : box.y - 26;
  const cursorStyle = {
    "--tx": `${cx - 2}px`,
    "--ty": `${cy - 2}px`,
  } as CSSProperties;

  return (
    <Scene label={`Where to find ${label}`} className={styles.locator}>
      {/* page frame */}
      <rect
        className="ga-surface"
        x="6"
        y="8"
        width="228"
        height="186"
        rx="10"
      />

      {/* header */}
      <rect className="ga-wire" x="14" y="16" width="56" height="8" rx="3" />
      <rect
        className="ga-wire-dim"
        x="14"
        y="30"
        width="26"
        height="10"
        rx="3"
      />
      <rect
        className="ga-wire-dim"
        x="44"
        y="30"
        width="26"
        height="10"
        rx="3"
      />
      <rect
        className="ga-wire-success"
        x="78"
        y="17"
        width="28"
        height="12"
        rx="6"
      />
      <rect
        className="ga-wire-primary"
        x="112"
        y="17"
        width="32"
        height="12"
        rx="4"
      />
      {Object.values(HEADER_ICONS).map((x) => (
        <rect
          key={x}
          className="ga-wire"
          x={x}
          y="17"
          width="12"
          height="12"
          rx="3"
        />
      ))}

      {/* toolbar */}
      <rect
        className="ga-wire-deep"
        x="14"
        y="48"
        width="110"
        height="18"
        rx="9"
      />
      <rect
        className="ga-wire-deep"
        x="130"
        y="48"
        width="50"
        height="18"
        rx="5"
      />
      <rect
        className="ga-wire-deep"
        x="186"
        y="48"
        width="40"
        height="18"
        rx="5"
      />

      {/* grid */}
      {Array.from({ length: 6 }).map((_, k) => (
        <Tile
          key={k}
          x={14 + (k % 3) * 48}
          y={74 + Math.floor(k / 3) * 48}
          size={40}
        />
      ))}

      {/* right panel */}
      <rect
        className="ga-wire-deep"
        x="160"
        y="74"
        width="66"
        height="96"
        rx="8"
      />
      <rect className="ga-wire" x="164" y="78" width="28" height="12" rx="4" />
      <rect
        className="ga-wire-dim"
        x="194"
        y="78"
        width="28"
        height="12"
        rx="4"
      />
      {[0, 1, 2, 3].map((k) => (
        <rect
          key={k}
          className="ga-wire-dim"
          x="166"
          y={98 + k * 16}
          width={48 - k * 9}
          height="6"
          rx="3"
        />
      ))}

      {/* footer */}
      <rect
        className="ga-wire-dim"
        x="14"
        y="179"
        width="60"
        height="8"
        rx="3"
      />
      <rect
        className="ga-wire-dim"
        x="150"
        y="181"
        width="76"
        height="4"
        rx="2"
      />
      <circle className="ga-wire" cx="188" cy="183" r="4" />

      {/* highlight: dim everything except the target */}
      <mask id={maskId}>
        <rect x="0" y="0" width="240" height="200" fill="#fff" />
        <rect
          x={box.x - pad}
          y={box.y - pad}
          width={box.w + pad * 2}
          height={box.h + pad * 2}
          rx={Math.min(8, box.h / 2 + pad)}
          fill="#000"
        />
      </mask>
      <rect
        className="ga-dimmer"
        x="6"
        y="8"
        width="228"
        height="186"
        rx="10"
        mask={`url(#${maskId})`}
      />
      <rect
        className="ga-locate-ring"
        x={box.x - pad}
        y={box.y - pad}
        width={box.w + pad * 2}
        height={box.h + pad * 2}
        rx={Math.min(8, box.h / 2 + pad)}
      />
      <g className="ga-callout">
        <rect
          className="ga-callout-bg"
          x={calloutX}
          y={calloutY}
          width={calloutWidth}
          height="18"
          rx="6"
        />
        <text
          className="ga-text ga-callout-text"
          x={calloutX + calloutWidth / 2}
          y={calloutY + 12}
          fontSize="8"
          textAnchor="middle"
        >
          {label}
        </text>
      </g>
      <Cursor className="ga-cur-locate" style={cursorStyle} />
    </Scene>
  );
};
